import React, { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { cn } from '@/lib/utils';

interface OnboardingTooltipProps {
  title: string;
  content: string;
  children: React.ReactNode;
  triggerClassName?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  showOnFirstVisit?: boolean;
  storageKey?: string;
}

export const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({
  title,
  content,
  children,
  triggerClassName,
  side = 'bottom',
  showOnFirstVisit = false,
  storageKey
}) => {
  const [isOpen, setIsOpen] = useState(() => {
    if (showOnFirstVisit && storageKey) {
      return !localStorage.getItem(storageKey);
    }
    return false;
  });

  const handleClose = () => {
    setIsOpen(false);
    if (storageKey) {
      localStorage.setItem(storageKey, 'seen');
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative inline-block">
      <div className="flex items-center gap-2">
        {children}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleToggle}
          className={cn("p-1 h-6 w-6 rounded-full", triggerClassName)}
        >
          <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
        </Button>
      </div>

      {isOpen && (
        <>
          {/* Mobile Sheet Overlay */}
          <div className="md:hidden fixed inset-0 bg-black/50 z-50" onClick={handleClose} />
          
          {/* Mobile Sheet */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 animate-slide-in-bottom">
            <Card className="rounded-t-xl rounded-b-none">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{title}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={handleClose}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pb-6">
                <p className="text-muted-foreground leading-relaxed">{content}</p>
                <Button onClick={handleClose} className="w-full mt-4">
                  Got it!
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Desktop Popover */}
          <div className={cn(
            "hidden md:block absolute z-50 w-80 animate-fade-in",
            {
              "bottom-full mb-2": side === 'top',
              "top-full mt-2": side === 'bottom',
              "right-full mr-2": side === 'left',
              "left-full ml-2": side === 'right',
            }
          )}>
            <Card className="shadow-lg border">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{title}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={handleClose}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">{content}</p>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};